/**
 * 帮助面板（M5-05 / DESIGN.md §7.5、§1.4）。
 *
 * 常用操作速查 + 隐私说明的入口，不是重新发明一份快捷键表——那张表已经
 * 是 `ShortcutEditorDialog`（M5-03）在做的事，这里的"查看全部快捷键…"
 * 直接打开它，速查区自己只挑一小撮最常用的动作，且这几条也是从
 * `props.actions`（调用方传入"套过用户快捷键覆盖层的有效动作列表"）
 * 现读快捷键文案，不是写死的静态字符串——改绑之后这里跟着变。
 */
import { For, Show, type JSX } from 'solid-js';
import type { ActionDefinition } from '../../app/actions/registry';
import { formatShortcut, type Platform } from '../../app/actions/keymap';
import { useFocusTrap } from '../a11y/focus-trap';
import styles from './HelpPanel.module.css';

/** 速查区挑的这几个动作 id——覆盖"打开/编辑/变换/导出"这几类 Photoshop
 * 用户下意识就会找的操作，不追求穷举（穷举就是 `ShortcutEditorDialog`
 * 该干的事，速查区的价值在于"少而准"）。 */
const QUICK_REFERENCE_IDS: readonly string[] = [
  'file.new',
  'file.open',
  'file.save',
  'file.export',
  'edit.undo',
  'edit.freeTransform',
  'layer.new',
  'layer.duplicate',
  'select.all',
  'select.deselect',
];

export interface HelpPanelProps {
  readonly open: boolean;
  readonly actions: readonly ActionDefinition[];
  readonly platform?: Platform | undefined;
  readonly onClose: () => void;
  readonly onOpenShortcutEditor: () => void;
  readonly onOpenAbout: () => void;
}

export function HelpPanel(props: HelpPanelProps): JSX.Element {
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  const quickReference = (): readonly ActionDefinition[] => {
    const byId = new Map(props.actions.map((a) => [a.id, a] as const));
    return QUICK_REFERENCE_IDS.map((id) => byId.get(id)).filter(
      (a): a is ActionDefinition => a !== undefined,
    );
  };

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} onClick={props.onClose} data-testid="help-panel-backdrop">
        <div
          ref={(el) => {
            dialogRef = el;
          }}
          class={styles.dialog}
          role="dialog"
          aria-label="帮助"
          data-testid="help-panel-dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <header class={styles.header}>
            <h2 class={styles.title}>帮助</h2>
            <button
              type="button"
              class={styles.close}
              onClick={props.onClose}
              aria-label="关闭"
              data-testid="help-panel-close"
            >
              ✕
            </button>
          </header>

          <section class={styles.section}>
            <h3 class={styles.sectionTitle}>常用操作速查</h3>
            <ul class={styles.quickList} data-testid="help-panel-quick-reference">
              <For each={quickReference()}>
                {(action) => (
                  <li class={styles.quickItem}>
                    <span>{action.title}</span>
                    <Show when={action.shortcut} fallback={<span class={styles.noShortcut}>—</span>}>
                      {(shortcut) => <kbd class={styles.kbd}>{formatShortcut(shortcut(), props.platform)}</kbd>}
                    </Show>
                  </li>
                )}
              </For>
            </ul>
            <button
              type="button"
              class={styles.linkButton}
              onClick={props.onOpenShortcutEditor}
              data-testid="help-panel-open-shortcuts"
            >
              查看/自定义全部快捷键…
            </button>
          </section>

          <section class={styles.section}>
            <h3 class={styles.sectionTitle}>隐私</h3>
            <p class={styles.prose}>
              你的图片不会离开这台设备。首屏加载完成后本应用不发出任何网络请求，这一点由内容安全策略强制，并有自动化测试持续验证。
            </p>
            <button
              type="button"
              class={styles.linkButton}
              onClick={props.onOpenAbout}
              data-testid="help-panel-open-about"
            >
              查看完整隐私承诺与开源许可…
            </button>
          </section>
        </div>
      </div>
    </Show>
  );
}
